/**
 * Response of fulfilment `GET /operations-stats/stock-requirements` and `/remaining`,
 * passed through unchanged by the fulfilment BFF to the Stock Requirement page.
 */
import { z } from 'zod'
import { Brand } from '../../shared/enums'
import { PouchesPerCrateSchema, TygaPouchesPerCrateSchema } from './meal-crate-config'

/** Crates needed for one protein, keyed by pouch size. */
export const ProteinCratesSchema = z.object({
  name: z.string(),
  quantities: z.record(z.string(), z.number()),
})
export type ProteinCrates = z.infer<typeof ProteinCratesSchema>

const MealTypeSectionSchema = z.object({
  pouchesPerCrate: PouchesPerCrateSchema,
  proteins: z.record(z.string(), ProteinCratesSchema),
})

/**
 * One extras row. Brands can share a SKU (Tyga boxes record Lyka's `wool_insulation`),
 * so a SKU can appear once per brand: tell rows apart by brand and SKU, not SKU alone.
 */
export const StockSummaryEntrySchema = z.object({
  sku: z.string(),
  name: z.string(),
  productVariantName: z.string(),
  productShortCode: z.string().optional(),
  quantity: z.number(),
  brand: z.nativeEnum(Brand),
})
export type StockSummaryEntry = z.infer<typeof StockSummaryEntrySchema>

export const StockRequirementResponseSchema = z.object({
  mealCrates: z.object({
    smooth: MealTypeSectionSchema,
    chunky: MealTypeSectionSchema,
  }),
  extras: z.object({
    treats: z.array(StockSummaryEntrySchema),
    supplements: z.array(StockSummaryEntrySchema),
    collateral: z.array(StockSummaryEntrySchema),
    merch: z.array(StockSummaryEntrySchema),
  }),
  tyga: z.object({
    pouchesPerCrate: TygaPouchesPerCrateSchema,
    proteins: z.record(z.string(), ProteinCratesSchema),
  }),
})
export type StockRequirementResponse = z.infer<typeof StockRequirementResponseSchema>
